/**
 * Opportunities — fieldiq-api `src/routes/opportunities.routes.ts` and
 * `src/lib/opportunity-write.ts` (CRM-API-3, api#1053; link routes api#1056).
 * Hand-written: the API manifest has not recorded these routes.
 *
 * READS camelCase, WRITES snake_case. `estimatedValue: null` means NOT
 * ESTIMATED — never 0 — and pipeline totals carry the unvalued count beside
 * the known value rather than folding it in.
 */
export const OPPORTUNITY_STAGES = ['lead', 'qualified', 'quoted', 'won', 'lost'] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];
export const OPEN_STAGES: readonly OpportunityStage[] = ['lead', 'qualified', 'quoted'];

/** opportunity-write.ts LOST_REASONS (0542). A CODE; free text goes in lost_reason_note. */
export const LOST_REASONS = ['price', 'competitor', 'timing', 'no_work', 'relationship', 'other'] as const;
export type LostReason = (typeof LOST_REASONS)[number];

/** opportunity-write.ts LEAD_SOURCES (0542). */
export const LEAD_SOURCES = ['referral', 'msa', 'existing_customer', 'tender', 'rig_activity', 'cold', 'other'] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export interface OpportunityLatestQuote {
  id: string;
  quoteNumber: string | null;
  status: string;
  totalAmount: number | null;
  currency: string | null;
}

export interface Opportunity {
  id: string;
  title: string;
  stage: string;
  customerId: string;
  customerName: string | null;
  customerStatus: string | null;
  ownerUserId: string;
  ownerName: string | null;
  contactId: string | null;
  contactName: string | null;
  estimatedValue: number | null;
  currency: string | null;
  expectedCloseDate: string | null;
  vertical: string | null;
  rigName: string | null;
  notes: string | null;
  /** A LostReason code on rows written since 0542; older rows may hold free text. */
  lostReason: string | null;
  lostReasonNote: string | null;
  nextStep: string | null;
  /** Calendar day 'YYYY-MM-DD'. */
  nextStepDue: string | null;
  /** EFFECTIVE probability: the override, else the stage default (api#1170). */
  probability: number;
  probabilityIsDefault: boolean;
  wellName: string | null;
  padOrLsd: string | null;
  area: string | null;
  competitor: string | null;
  /** Calendar day 'YYYY-MM-DD'. */
  expectedStart: string | null;
  leadSource: string | null;
  wonAt: string | null;
  lostAt: string | null;
  source: string;
  createdAt: string;
  updatedAt: string;
  latestQuote: OpportunityLatestQuote | null;
}

export interface OpportunityStageHistory {
  id: string;
  fromStage: string | null;
  toStage: string;
  changedBy: string | null;
  changedByName: string | null;
  agentRunId: string | null;
  changedAt: string;
  estimatedValueAtChange: number | null;
}

export interface OpportunityDetail extends Opportunity {
  stageHistory: OpportunityStageHistory[];
  quotes: { id: string; quoteNumber: string | null; status: string; totalAmount: number | null; currency: string | null; createdAt: string }[];
  jobs: { id: string; jobNumber: string | null; status: string; scheduledDate: string | null; createdAt: string }[];
  meetings: { id: string; kind: string; occurredAt: string; userId: string; notes: string | null; nextStep: string | null }[];
  /** Scout's open stage proposal for this deal (api#1174); at most one is pending. */
  pendingStageProposal: StageProposal | null;
}

/** GET /opportunities/stage-proposals row (api#1174). A PENDING Scout run; the deal has not moved. */
export interface StageProposal {
  runId: string;
  opportunityId: string | null;
  title: string | null;
  customerName: string | null;
  ownerUserId: string | null;
  fromStage: string | null;
  toStage: string | null;
  reason: string | null;
  lostReason: string | null;
  lostReasonNote: string | null;
  proposedBy: string | null;
  proposedAt: string;
}

export interface StageProposalDecision {
  runId: string;
  decision: 'approve' | 'reject' | string;
  opportunityId: string;
  fromStage?: string;
  toStage?: string;
  promotedFrom?: 'prospect' | 'inactive' | null;
}

/** POST/PATCH result: the row plus whether entering `won` promoted the customer. */
export interface OpportunityWriteResult extends Opportunity {
  customerPromotedFrom: 'prospect' | 'inactive' | null;
}

export interface PipelineStage {
  stage: string;
  count: number;
  valueKnownTotal: number;
  valueUnknownCount: number;
  /** Σ value × probability / 100 over KNOWN values only (api#1170). */
  weightedValueKnownTotal: number;
  defaultProbability: number;
}

export interface Pipeline {
  stages: PipelineStage[];
  period: { from: string; to: string; timeZone: string; wonCount: number; lostCount: number };
}

export interface OpportunityLinkResult {
  opportunityId: string;
  quoteId?: string;
  jobId?: string;
  linked: boolean;
  /** 'quoted' when a quote was linked while the stage was lead/qualified (D16) — a suggestion, never applied. */
  suggestedStage: 'quoted' | null;
}

export interface OpportunityWriteBody {
  customer_id?: string;
  title?: string;
  stage?: OpportunityStage;
  owner_user_id?: string;
  contact_id?: string | null;
  estimated_value?: number | null;
  expected_close_date?: string | null;
  vertical?: string | null;
  rig_name?: string | null;
  notes?: string | null;
  lost_reason?: LostReason | null;
  lost_reason_note?: string | null;
  next_step?: string | null;
  next_step_due?: string | null;
  /** int 0–100, or null = back to the stage default. */
  probability?: number | null;
  well_name?: string | null;
  pad_or_lsd?: string | null;
  area?: string | null;
  expected_start?: string | null;
  competitor?: string | null;
  lead_source?: LeadSource | null;
}

export interface OpportunityListParams {
  stage?: string;
  customer_id?: string;
  mine?: 1;
  owner?: string;
}

export interface PipelineParams {
  include_closed?: 1;
  mine?: 1;
  owner?: string;
}
